/**
 * Replace chat privacy tokens (<<ACC:n>>, <<INS:n>>) with user-visible labels.
 * Tokens are assigned server-side per session; the map is token → display text.
 */

/** Complete privacy tokens the session map doesn't know. */
const LEFTOVER_TOKEN_RE = /<<(INS|ACC):\d+>>/g;

/**
 * Any other <<...>> fragment — malformed/hallucinated token syntax. The server
 * drops these before streaming or logging, but messages logged before that
 * scrub existed can still surface via session resume, so the client mirrors it.
 */
const MALFORMED_TOKEN_RE = /<<(?!(?:INS|ACC):\d+>>)[^<>]*>>/g;

/** Longest plausible well-formed token, e.g. <<ACC:99999>> (mirrors the server). */
const MAX_TOKEN_LEN = 24;

/**
 * A trailing fragment that could still grow into a valid token as chunks
 * arrive: "<<", "<<A", "<<ACC", "<<ACC:", "<<ACC:12", "<<ACC:12>". Anything
 * else after "<<" (e.g. "<< 10") is ordinary text and is rendered as-is.
 */
const PARTIAL_TOKEN_PREFIX_RE = /^<<(?:I(?:NS?)?|A(?:CC?)?)?$|^<<(?:INS|ACC):\d*>?$/;

/** Generic nouns for tokens that can't be rehydrated — a raw token must never reach the DOM. */
const TOKEN_FALLBACK: Record<string, string> = {
  ACC: 'your account',
  INS: 'your bank',
};

export function applyChatPrivacyLexicon(
  text: string,
  map: Record<string, string> | undefined | null,
): string {
  if (!text) return text;
  let out = text;
  if (map && Object.keys(map).length > 0) {
    const keys = Object.keys(map).sort((a, b) => b.length - a.length);
    for (const token of keys) {
      const label = map[token];
      if (token && label) {
        out = out.split(token).join(label);
      }
    }
  }
  // Backstop mirrors the server scrub: an unmapped complete token degrades to
  // a generic noun (the map may not have arrived, or the token slipped
  // through), and malformed <<...>> fragments are dropped. Raw internal token
  // syntax must never reach the DOM.
  out = out.replace(LEFTOVER_TOKEN_RE, (_m, kind: string) => TOKEN_FALLBACK[kind]);
  return out.replace(MALFORMED_TOKEN_RE, '');
}

/**
 * While SSE tokens stream in, hold an incomplete trailing `<<ACC:…` out of the
 * rendered text until it completes — appending it raw would flash (or, if the
 * stream dies mid-token, permanently strand) an internal token in the UI.
 * Trailing "<<" fragments that can't become a token render as ordinary text.
 */
export function applyChatPrivacyLexiconStreamingSafe(
  text: string,
  map: Record<string, string> | undefined | null,
): string {
  if (!text) return text;
  const lastOpen = text.lastIndexOf('<<');
  if (lastOpen !== -1) {
    const tail = text.slice(lastOpen);
    if (!tail.includes('>>') && tail.length <= MAX_TOKEN_LEN && PARTIAL_TOKEN_PREFIX_RE.test(tail)) {
      return applyChatPrivacyLexicon(text.slice(0, lastOpen), map);
    }
  }
  return applyChatPrivacyLexicon(text, map);
}

export function mergePrivacyLexicons(
  base: Record<string, string> | undefined,
  incoming: Record<string, string> | undefined,
): Record<string, string> {
  if (!incoming || Object.keys(incoming).length === 0) return { ...(base || {}) };
  return { ...(base || {}), ...incoming };
}
